import { IconSelectClean } from "@components/inputs/select/IconSelect";
import { ISelectItem, TSelectItemId } from "@components/inputs/select/Select.types";
import {
    getSimpleBoolSelectItems,
    ifAll,
    IFieldInfoProperties,
    IGetValueArgs,
    TGetValueFn,
    TTemporalDialogSettings
} from "@components/smart/FieldInfo";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import {
    EntityTypeName,
    IPrEmploymentEntity,
    IPrSalaryComponentEntity,
    PrEmploymentEntity,
    PrEmploymentSalaryComponentEntity,
    PrEmploymentSalaryComponentTemporalEntity,
    PrEmploymentTemplateEntity,
    PrEmploymentTemplateSalaryComponentEntity,
    PrEmploymentTemplateSalaryComponentTemporalEntity,
    PrFixedBonusPayOutImpactEntity,
    PrFixedBonusTaxImpactEntity,
    PrPayslipEmploymentSalaryComponentEntity,
    PrRegularSalaryTypeEntity,
    PrSalaryComponentAverageInclusionEntity,
    PrSalaryComponentEntity,
    PrSalaryComponentTemporalCurrentEntity,
    PrSalaryComponentTemporalEntity,
    PrSalaryComponentTypeEntity,
    PrTimeBasedBonusTypeEntity,
    TemporalEntityEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrEmploymentTypeCode,
    PrFixedBonusDeductionImpactCode,
    PrFixedBonusPayOutImpactCode,
    PrFixedBonusTaxImpactCode,
    PrRegularSalaryTypeCode,
    PrSalaryComponentAverageInclusionCode,
    PrSalaryComponentTypeCode,
    PrTimeBasedBonusTypeCode
} from "@odata/GeneratedEnums";
import { getEnumNameSpaceName, getEnumSelectItems } from "@odata/GeneratedEnums.utils";
import { transformToODataString } from "@odata/OData.utils";
import {
    getFakeTemporalFieldFormatter,
    getSyncedLineItemHistoryValues,
    getTemporalDataColumns,
    getTemporalPropertyExtraFieldContentAfter,
    getTemporalPropertyLineItemFieldDefinition,
    IGetTemporalPropertyLineItemFieldDefinitionArgs,
    isFieldSynchronized,
    TemporalEntityProp
} from "@odata/TemporalUtils";
import { TFieldsDefinition } from "@pages/PageUtils";
import { getPayrollCurrencyFieldDef } from "@pages/payroll/payroll.utils";
import {
    DEFAULT_FULL_TIME_HOURS_PER_WEEK,
    getWeeklyWorkingHours
} from "@pages/payroll/workingPatterns/WorkingPatterns.utils";
import i18next from "i18next";

import {
    BasicInputSizes,
    FieldType,
    GroupedField,
    QueryParam,
    ToolbarItemType,
    ValidatorType,
    ValueType
} from "../../../enums";
import { TableStorage } from "../../../model/TableStorage";
import { getQueryParameters } from "../../../routes/Routes.utils";
import { getUtcDayjs } from "../../../types/Date";
import { ICustomButtonDefArgs, TableActionOrder, TTableToolbarItem } from "../../../views/table/TableToolbar.utils";
import { SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";

export const ADD_SALARY_COMPONENT_BUTTON = "SalaryComponentAddButton";

// we want to show Type and RegularSalaryType value in SmartTemporalPropertyDialog,
// even though it is not a temporal value => use local context
export const FakeTypePath = BindingContext.localContext("Type");
export const FakeRegularTypePath = BindingContext.localContext("RegularSalaryType");

export function getSalaryComponentTypesForForm(): PrSalaryComponentTypeCode[] {
    return [
        PrSalaryComponentTypeCode.Fixed,
        PrSalaryComponentTypeCode.PercentageBonus,
        PrSalaryComponentTypeCode.Regular,
    ];
}

export const SalaryEnums = [
    EntityTypeName.PrSalaryComponentType,
    EntityTypeName.PrRegularSalaryType,
    EntityTypeName.PrFixedBonusTaxImpact,
    EntityTypeName.PrFixedBonusPayOutImpact,
    EntityTypeName.PrFixedBonusDeductionImpact,
    // EntityTypeName.PrIncomeType,
    EntityTypeName.CsszEmploymentType
];

export const getSalaryEnumNamespaceNames = (): string[] => SalaryEnums.map(getEnumNameSpaceName);

export enum SalaryComponentTab {
    Active = "1",
    Inactive = "2"
}

export function getFilterForTabId(tabId: string): string {
    const nowDate = transformToODataString(getUtcDayjs().startOf("day"), ValueType.Date);
    const DateValidToPath = createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalCurrentEntity.DateValidTo);
    switch (tabId as SalaryComponentTab) {
        case SalaryComponentTab.Active:
            return `(${DateValidToPath} ge ${nowDate} or ${DateValidToPath} eq null)`;
        case SalaryComponentTab.Inactive:
            return `${DateValidToPath} lt ${nowDate}`;
        case SECONDARY_FILTERS_ALL:
        default:
            return "";
    }
}

function getIconForSalaryComponentType(type: PrSalaryComponentTypeCode): string {
    switch (type) {
        case PrSalaryComponentTypeCode.Fixed:
            return "FixedSalaryComponent";
        case PrSalaryComponentTypeCode.Regular:
            return "RegularSalaryComponent";
        case PrSalaryComponentTypeCode.PercentageBonus:
            return "PercentageBonusSalaryComponent";
        case PrSalaryComponentTypeCode.TimeBasedBonus:
            return "TimeBasedBonusSalaryComponent";
        default:
            return undefined;
    }
}

/**
 * Configuration of add button icon select with all salary component types
 * @param storage
 * @param ref
 * @param args
 */
export function getToolbarAddButtonConfig(storage: TableStorage, ref: React.RefObject<IconSelectClean>, args?: ICustomButtonDefArgs): TTableToolbarItem {
    const supportedTypes = getSalaryComponentTypesForForm();
    const items = getEnumSelectItems(EntityTypeName.PrSalaryComponentType)
            .filter(item => supportedTypes.includes(item.id as PrSalaryComponentTypeCode))
            .map(item => ({
                ...item,
                iconName: getIconForSalaryComponentType(item.id as PrSalaryComponentTypeCode),
            }));

    return {
        id: ADD_SALARY_COMPONENT_BUTTON,
        itemType: ToolbarItemType.IconSelect,
        iconName: "Add",
        label: i18next.t("SalaryComponent:Table.Add"),
        order: TableActionOrder.Add,
        itemProps: {
            isTransparent: true,
            selectProps: {
                isActive: storage.data.addingRow || args?.isActive,
                isDisabled: !!storage.tableAction || args?.isDisabled,
                ref
            },
            items: [...items]
        }
    };
}

export const areAllSalaryComponentsOfSameType = (args: IGetValueArgs): boolean => {
    const { storage, bindingContext } = args;
    const salaryComponents = storage.getValue(bindingContext) ?? [];
    if (salaryComponents.length < 2) {
        return true;
    }
    const _getProp = (e: IEntity) => e?.TypeCode ?? e?.Type?.Code;
    const refType = _getProp(salaryComponents[0]);
    return !salaryComponents.slice(1).find((sc: IEntity) => _getProp(sc) !== refType);
};

function getSalaryComponentBc(bindingContext: BindingContext): BindingContext {
    // used in different contexts (employment, employment template, salary component or payslip), but parentBc should be always the salary component
    let parentBc = bindingContext.getParent();
    if (parentBc.getPath(true) === TemporalEntityProp.TemporalPropertyBag ||
        parentBc.getPath(true) === TemporalEntityProp.CurrentTemporalPropertyBag) {
        // For temporal properties, we need to navigate to main entity first
        parentBc = parentBc.getParent();
    }

    return parentBc;
}

export function isSalaryComponentType(...types: PrSalaryComponentTypeCode[]): TGetValueFn<boolean> {
    return (args: IGetValueArgs) => {
        const { storage, bindingContext } = args;
        // used in different contexts (employment, employment template, salary component or payslip), but parentBc should be always the salary component
        const salaryComponentBc = getSalaryComponentBc(bindingContext);
        let component: IPrSalaryComponentEntity;
        if (salaryComponentBc.isCollection() && !salaryComponentBc.getKey()) {
            component = args.storage.getValue(salaryComponentBc)?.[0];
        } else {
            component = storage.getValue(salaryComponentBc);
        }
        const refCode = component?.Type?.Code as PrSalaryComponentTypeCode;
        return !refCode || types.includes(refCode);
    };
}

export enum SalaryComponentVariant {
    Base = "Base",
    Employment = "Employment",
    EmploymentTemplate = "EmploymentTemplate",
    Payslip = "Payslip"
}

export function getSalaryComponentColumns(type: SalaryComponentVariant = SalaryComponentVariant.Base): string[] {
    const SalaryComponentEntity = type === SalaryComponentVariant.Employment ? PrEmploymentSalaryComponentTemporalEntity
        : type === SalaryComponentVariant.EmploymentTemplate ? PrEmploymentTemplateSalaryComponentTemporalEntity : PrSalaryComponentTemporalEntity;

    return [
        SalaryComponentEntity.Name,
        FakeTypePath,
        FakeRegularTypePath,
        SalaryComponentEntity.IsBasicSalary,
        SalaryComponentEntity.Percentage,
        SalaryComponentEntity.FixedBonusTaxImpact,
        SalaryComponentEntity.FixedBonusPayOutImpact,
        SalaryComponentEntity.FixedBonusDeductionImpact,
        SalaryComponentEntity.Amount,
    ];
}

// needs to be in callback, so tht translation is loaded
export const getSalaryComponentTemporalDialogSettings = (type: SalaryComponentVariant = SalaryComponentVariant.Base, withHistory = true): TTemporalDialogSettings => {
    return {
        dialogTitle: i18next.t("SalaryComponent:Form.PlanSalaryComponentTitle"),
        columns: getSalaryComponentColumns(type),
        extractHistory: withHistory ? getSyncedLineItemHistoryValues : undefined,
    };
};

export function getSalaryComponentFieldDefinition(propName: string, temporalDialogSettings?: TTemporalDialogSettings, type = SalaryComponentVariant.Base): Partial<IFieldInfoProperties> {
    let def: Partial<IFieldInfoProperties> = {};
    switch(propName) {
        case FakeTypePath:
            def = {
                width: BasicInputSizes.M,
                label: i18next.t("SalaryComponent:Form.Type"),
                isReadOnly: true,
                customizationData: {
                    useForCustomization: false
                },
                formatter: getFakeTemporalFieldFormatter(PrSalaryComponentEntity.Type)
            };
            break;
        case FakeRegularTypePath:
            def = {
                width: BasicInputSizes.M,
                label: i18next.t("SalaryComponent:Form.RegularType"),
                isReadOnly: true,
                customizationData: {
                    useForCustomization: false
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Regular),
                formatter: getFakeTemporalFieldFormatter(PrSalaryComponentEntity.RegularSalaryType)
            };
            break;
        case PrSalaryComponentTemporalEntity.IsBasicSalary:
            def = {
                type: FieldType.ComboBox,
                isVisible: ifAll(isSalaryComponentType(PrSalaryComponentTypeCode.Regular), args => {
                    const salaryComponentBc = getSalaryComponentBc(args.bindingContext);
                    const parent = args.storage.getValue(salaryComponentBc) as IPrSalaryComponentEntity;
                    return parent?.RegularSalaryType?.Code === PrRegularSalaryTypeCode.Monthly;
                }),
                defaultValue: true,
                isRequired: true,
                clearIfInvisible: true,
                fieldSettings: {
                    items: getSimpleBoolSelectItems(),
                }
            };
            break;
        case PrSalaryComponentTemporalEntity.Name:
            def = {
                width: BasicInputSizes.L,
                isRequired: true
            };
            break;
        case PrSalaryComponentEntity.Type:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                defaultValue: (args: IGetValueArgs) => {
                    return getQueryParameters()[QueryParam.Type] ?? PrSalaryComponentTypeCode.Regular;
                },
                fieldSettings: {
                    displayName: PrSalaryComponentTypeEntity.Name,
                },
                isRequired: true,
                affectedFields: [{
                    id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.IsBasicSalary),
                    navigateFromParent: true
                }]
            };
            break;
        case PrSalaryComponentEntity.RegularSalaryType:
            const _getEmploymentType = (args: IGetValueArgs): PrEmploymentTypeCode => {
                const parent = args.storage.getValue(args.bindingContext.getParent().getParent());
                const employment = (type === SalaryComponentVariant.Payslip ? parent.Employment : parent) as IPrEmploymentEntity;
                return employment?.Type?.Code as PrEmploymentTypeCode;
            };
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                isDisabled: (args) => {
                    if (type === SalaryComponentVariant.Base || !args.storage.getValue(args.bindingContext)) {
                        return false;
                    }
                    const employmentTypeCode = _getEmploymentType(args);
                    // only one item in the list, disable
                    return [PrEmploymentTypeCode.Compensation, PrEmploymentTypeCode.WorkAgreementDPP].includes(employmentTypeCode);
                },
                fieldSettings: {
                    displayName: PrRegularSalaryTypeEntity.Name,
                    itemsForRender: (items, args) => {
                        if (type === SalaryComponentVariant.Base) {
                            return items;
                        }
                        const employmentTypeCode = _getEmploymentType(args);
                        if (employmentTypeCode === PrEmploymentTypeCode.Compensation) {
                            return items.filter(item => item.id === PrRegularSalaryTypeCode.Monthly);
                        }
                        if (employmentTypeCode === PrEmploymentTypeCode.WorkAgreementDPP) {
                            return items.filter(item => item.id === PrRegularSalaryTypeCode.Hourly);
                        }
                        return items;
                    }
                },
                defaultValue: (args) => {
                    if (type === SalaryComponentVariant.Base) {
                        return PrRegularSalaryTypeCode.Monthly;
                    }
                    const employmentTypeCode = _getEmploymentType(args);
                    if (employmentTypeCode === PrEmploymentTypeCode.WorkAgreementDPP) {
                        return PrRegularSalaryTypeCode.Hourly;
                    }
                    return PrRegularSalaryTypeCode.Monthly;
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Regular),
                isRequired: true,
                affectedFields: [{
                    id: createPath(PrSalaryComponentEntity.CurrentTemporalPropertyBag, PrSalaryComponentTemporalEntity.IsBasicSalary),
                    navigateFromParent: true
                }]
            };
            break;
        case PrSalaryComponentTemporalEntity.FixedBonusTaxImpact:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                defaultValue: PrFixedBonusTaxImpactCode.Full,
                fieldSettings: {
                    displayName: PrFixedBonusTaxImpactEntity.Name
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Fixed)
            };
            break;
        case PrSalaryComponentTemporalEntity.FixedBonusPayOutImpact:
            def = {
                width: BasicInputSizes.S,
                type: FieldType.ComboBox,
                defaultValue: PrFixedBonusPayOutImpactCode.ToPayOut,
                fieldSettings: {
                    displayName: PrFixedBonusPayOutImpactEntity.Name,
                    itemsFactory: async (args: IGetValueArgs): Promise<ISelectItem<TSelectItemId>[]> => {
                        return getEnumSelectItems(EntityTypeName.PrFixedBonusPayOutImpact).map(item => ({
                            ...item,
                            label: i18next.t("SalaryComponent:FixedBonusPayOutImpact." + item.id)
                        }));
                    }
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Fixed)
            };
            break;
        case PrSalaryComponentTemporalEntity.FixedBonusDeductionImpact:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                defaultValue: PrFixedBonusDeductionImpactCode.NotIncluded,
                fieldSettings: {
                    displayName: "Name",
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.Fixed)
            };
            break;
        case PrPayslipEmploymentSalaryComponentEntity.TimeBasedBonusType:
            def = {
                width: BasicInputSizes.S,
                type: FieldType.ComboBox,
                defaultValue: PrTimeBasedBonusTypeCode.Monthly,
                label: i18next.t("SalaryComponent:Form.TimeBasedBonusType"),
                fieldSettings: {
                    displayName: PrTimeBasedBonusTypeEntity.Name,
                    itemsFactory: async (args: IGetValueArgs): Promise<ISelectItem<TSelectItemId>[]> => {
                        return getEnumSelectItems(EntityTypeName.PrTimeBasedBonusType).map(item => ({
                            ...item,
                            label: i18next.t("SalaryComponent:TimeBasedBonusType." + item.id)
                        }));
                    }
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.TimeBasedBonus)
            };
            break;
        case PrPayslipEmploymentSalaryComponentEntity.AverageInclusion:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.ComboBox,
                label: i18next.t("SalaryComponent:Form.AverageInclusion"),
                defaultValue: PrSalaryComponentAverageInclusionCode.Include,
                fieldSettings: {
                    displayName: PrSalaryComponentAverageInclusionEntity.Name,
                    itemsFactory: async (args: IGetValueArgs): Promise<ISelectItem<TSelectItemId>[]> => {
                        return getEnumSelectItems(EntityTypeName.PrSalaryComponentAverageInclusion).map(item => ({
                            ...item,
                            label: i18next.t("SalaryComponent:AverageInclusion." + item.id)
                        }));
                    }
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.TimeBasedBonus)
            };
            break;
        case PrSalaryComponentTemporalEntity.Percentage:
            def = {
                width: BasicInputSizes.M,
                type: FieldType.NumberInput,
                label: i18next.t("SalaryComponent:Percentage"),
                defaultValue: 100,
                fieldSettings: {
                    unit: "%",
                    showSteppers: true,
                    min: 0,
                    max: 100
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        max: 100
                    }
                },
                isVisible: isSalaryComponentType(PrSalaryComponentTypeCode.PercentageBonus),
                isRequired: true
            };
            break;
        case PrSalaryComponentTemporalEntity.Amount:
            def = {
                width: BasicInputSizes.S,
                ...getPayrollCurrencyFieldDef(),
                fieldSettings: {
                    ...getPayrollCurrencyFieldDef().fieldSettings,
                    min: 0
                },
                validator: {
                    type: ValidatorType.Number,
                    settings: {
                        min: 0,
                        excludeMin: true
                    }
                },
                isRequired: true,
            };
            break;
    }

    return temporalDialogSettings ? {
        ...def,
        fieldSettings: {
            ...def.fieldSettings,
            temporalDialog: temporalDialogSettings
        }
    } : def;
}

const isSaved: TGetValueFn<boolean> = (args: IGetValueArgs) => !args.storage.data.bindingContext.isNew();

export function getSalaryComponentFieldDefinitions(type: SalaryComponentVariant = SalaryComponentVariant.Base, options: { moveAmountOpeningButtonToAdjustedAmount?: boolean, adjustedAmountFieldId?: string } = {}): TFieldsDefinition {
    const isEmployment = type === SalaryComponentVariant.Employment;
    const isTemplate = type === SalaryComponentVariant.EmploymentTemplate;
    const isBase = type === SalaryComponentVariant.Base;

    const MainEntity = isTemplate ? PrEmploymentTemplateEntity : (isEmployment ? PrEmploymentEntity : null);
    const SalaryComponentEntity = isEmployment ? PrEmploymentSalaryComponentEntity
        : isTemplate ? PrEmploymentTemplateSalaryComponentEntity : PrSalaryComponentEntity;
    const SalaryComponentTemporalEntity = isEmployment ? PrEmploymentSalaryComponentTemporalEntity
        : isTemplate ? PrEmploymentTemplateSalaryComponentTemporalEntity : PrSalaryComponentTemporalEntity;

    const collectionName = MainEntity ? MainEntity.SalaryComponents : null;
    const isSynchronized = !!collectionName;

    const temporalDialogSettings: TTemporalDialogSettings = getSalaryComponentTemporalDialogSettings(type, !isBase);

    const commonDefs: Omit<IGetTemporalPropertyLineItemFieldDefinitionArgs, "propName"> = {
        collectionName, withOpeningButton: false, isSynchronized
    };

    const columns = getSalaryComponentColumns(type);
    const dataColumns = getTemporalDataColumns(columns);

    // additional properties, which are attached to the amount field
    const additionalProperties = isBase ? undefined
        : dataColumns.map(id => ({ id: `/${MainEntity.SalaryComponents}/${PrSalaryComponentEntity.TemporalPropertyBag}/${id}` }));

    if (!isBase) {
        [TemporalEntityEntity.DateValidFrom, TemporalEntityEntity.DateValidTo].forEach(id => {
            additionalProperties.push({ id: `/${createPath(MainEntity.SalaryComponents, PrSalaryComponentEntity.CurrentTemporalPropertyBag, id)}`});
        });

        const WithDefaultEntity = isEmployment ? PrEmploymentSalaryComponentEntity : PrEmploymentTemplateSalaryComponentEntity;
        dataColumns.forEach(id => {
            additionalProperties.push({ id: `/${createPath(MainEntity.SalaryComponents, WithDefaultEntity.Default, PrSalaryComponentEntity.CurrentTemporalPropertyBag, id)}`});
            additionalProperties.push({ id: `/${createPath(MainEntity.SalaryComponents, WithDefaultEntity.Default, PrSalaryComponentEntity.TemporalPropertyBag, id)}`});
        });
    }

    const isExistingSalaryComponentLineItem = (args: IGetValueArgs) => {
        return !args.bindingContext.getParent().isNew();
    };

    const TypeDef = getSalaryComponentFieldDefinition(SalaryComponentEntity.Type, undefined, type);
    const RegularTypeDef = getSalaryComponentFieldDefinition(SalaryComponentEntity.RegularSalaryType, undefined, type);

    return {
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentTemporalEntity.Name,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentTemporalEntity.Name, temporalDialogSettings),
                additionalProperties,
                groupedField: GroupedField.WrappedMultiStart
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentTemporalEntity.IsBasicSalary,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentTemporalEntity.IsBasicSalary, temporalDialogSettings),
            }
        }),
        [createPath(collectionName, SalaryComponentEntity.Type)]: {
            ...TypeDef,
            isReadOnly: isBase ? true : (args: IGetValueArgs) => isExistingSalaryComponentLineItem(args) || isFieldSynchronized(args),
            fieldSettings: {
                ...TypeDef.fieldSettings,
                transformFetchedItems: (items: ISelectItem[]) => {
                    return items.filter(item => getSalaryComponentTypesForForm().includes(item.id as PrSalaryComponentTypeCode));
                },
                isSynchronized: isFieldSynchronized
            },
            ...(isBase ? {
                customizationData: {
                    useForCustomization: false
                }
            } : {}),
            ...(options.adjustedAmountFieldId ? { affectedFields: [{ id: options.adjustedAmountFieldId }] } : {})
        },
        [createPath(collectionName, SalaryComponentEntity.RegularSalaryType)]: {
            ...RegularTypeDef,
            isReadOnly: isBase ? isSaved : (args: IGetValueArgs) => isExistingSalaryComponentLineItem(args) || isFieldSynchronized(args),
            fieldSettings: {
                ...RegularTypeDef.fieldSettings,
                isSynchronized: isFieldSynchronized
            },
            ...(options.adjustedAmountFieldId ? { affectedFields: [{ id: options.adjustedAmountFieldId }] } : {})
        },
        [createPath(collectionName, TemporalEntityProp.TemporalPropertyBag, FakeTypePath)]: getSalaryComponentFieldDefinition(FakeTypePath),
        [createPath(collectionName, TemporalEntityProp.TemporalPropertyBag, FakeRegularTypePath)]: getSalaryComponentFieldDefinition(FakeRegularTypePath),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentTemporalEntity.FixedBonusTaxImpact,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentTemporalEntity.FixedBonusTaxImpact, temporalDialogSettings),
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentTemporalEntity.FixedBonusPayOutImpact,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentTemporalEntity.FixedBonusPayOutImpact, temporalDialogSettings),
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentTemporalEntity.FixedBonusDeductionImpact,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentTemporalEntity.FixedBonusDeductionImpact, temporalDialogSettings),
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentTemporalEntity.Percentage,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentTemporalEntity.Percentage, temporalDialogSettings),
            }
        }),
        ...getTemporalPropertyLineItemFieldDefinition({
            ...commonDefs,
            propName: SalaryComponentTemporalEntity.Amount,
            definition: {
                ...getSalaryComponentFieldDefinition(SalaryComponentTemporalEntity.Amount, temporalDialogSettings),
                groupedField: GroupedField.WrappedMultiEnd,
            },
            currentDefinition: {
                ...(options.adjustedAmountFieldId ? { affectedFields: [{ id: options.adjustedAmountFieldId }] } : {}),
                extraFieldContentAfter: (args) => {
                    if (options.moveAmountOpeningButtonToAdjustedAmount) {
                        const workingPattern = args.storage.getValueByPath(createPath("CurrentTemporalPropertyBag", "WorkingPattern"));
                        const weeklyHours = getWeeklyWorkingHours(workingPattern);
                        const hoursPerWeekFullTime = workingPattern?.HoursPerWeekFullTime ?? DEFAULT_FULL_TIME_HOURS_PER_WEEK;
                        if (weeklyHours !== null && weeklyHours < hoursPerWeekFullTime) {
                            const lineItemBc = args.bindingContext.getParent().getParent();
                            const salaryComponent = args.storage.getValue(lineItemBc) as IPrSalaryComponentEntity;
                            const regularSalaryTypeCode = salaryComponent?.RegularSalaryType?.Code;
                            if (regularSalaryTypeCode && regularSalaryTypeCode !== PrRegularSalaryTypeCode.Hourly) {
                                return null;
                            }
                        }
                    }
                    return getTemporalPropertyExtraFieldContentAfter()(args);
                }
            }
        })
    };
}